import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatImageUrl } from '../utils/imageUtils';
import SpeakerModal from './SpeakerModal';

interface Speaker {
  id: string;
  name: string;
  title: string;
  image?: string;
  company?: string;
  bio?: string;
  bio_short?: string;
  talk_title?: string;
  talk_abstract?: string;
  linkedin_url?: string;
}

interface FeaturedSpeakersProps {
  speakers: Speaker[];
  maxSpeakers?: number;
  showViewAllLink?: boolean;
  showHeader?: boolean;
}

const FeaturedSpeakers: React.FC<FeaturedSpeakersProps> = ({ 
  speakers, 
  maxSpeakers = 6,
  showViewAllLink = true,
  showHeader = true
}) => {
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (speakers.length === 0) {
    return null;
  }

  // Memoize random selection to prevent reshuffling on re-renders
  const displayedSpeakers = useMemo(() => {
    if (!showViewAllLink) return speakers;
    
    if (speakers.length <= maxSpeakers) return speakers;
    
    const shuffled = [...speakers].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, maxSpeakers);
  }, [speakers, maxSpeakers, showViewAllLink]);

  const handleSpeakerClick = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSpeaker(null);
  };

  return (
    <>
      <section className="mb-16">
        {showHeader && (
          <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Featured Speakers
          </h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedSpeakers.map((speaker) => (
            <div 
              key={speaker.id} 
              onClick={() => handleSpeakerClick(speaker)}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 h-full flex flex-col cursor-pointer"
            >
              <div className="text-center mb-4">
                <img 
                  src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
                  alt={speaker.name}
                  className="w-24 h-24 rounded-full mx-auto mb-3 object-cover border-4 border-gray-100"
                />
                <h3 className="text-xl font-semibold mb-1 text-gray-800">{speaker.name}</h3>
                {speaker.title && (
                  <p className="text-sm font-medium text-[#00529B] mb-1">{speaker.title}</p>
                )}
                {speaker.company && (
                  <p className="text-sm text-gray-600 mb-3">{speaker.company}</p>
                )}
              </div>
              
              <div className="flex-1 text-center">
                {speaker.bio_short && (
                  <div>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">{speaker.bio_short}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-center">
                <span className="text-xs text-[#00529B] font-medium">Click to learn more</span>
              </div>
            </div>
          ))}
        </div>
        
        {/* View All Speakers Link */}
        {showViewAllLink && speakers.length > maxSpeakers && (
          <div className="text-center mt-8">
            <Link
              to="/speakers"
              className="inline-flex items-center px-6 py-3 bg-[#00529B] text-white font-medium rounded-lg hover:bg-[#003D74] transition-colors duration-300"
            >
              View All Speakers ({speakers.length})
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        )}
      </section>

      <SpeakerModal 
        speaker={selectedSpeaker}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default FeaturedSpeakers;
